import { useMemo, useState } from 'react';
import { Survey } from 'survey-react-ui';
import { Model } from 'survey-core';
import { ClientForm } from '../../api/types/digitalForms.api.types';
import { Alert, Button, Stack, Text } from '@mantine/core';
import { useSubmitSurveyResponse } from '../../api/REST/useSubmitSurveyResponse';
import { useRegisterCustomQuestionTypes } from '../../questionTypes/useRegisterCustomQuestionTypes';
import { useMobileInputFix } from '../../hooks/useMobileInputFix';
import classesV1 from './SurveyFormWrapper.module.scss';
import classesV2 from './SurveyFormWrapperV2.module.scss';
import { getThemeJson } from '../../constants/digitalForm.theme';
import { applyResponseLimits } from '../../utils/responseLimits';

interface SurveyFormWrapperProps {
  clientForm: ClientForm;
  // Optional submit override — when provided, called instead of the default
  // client-form submission. Used by the online-scheduler digital forms
  // renderer to POST to its own backend.
  submitOverride?: {
    mutate: (vars: { uuid: string; responseJson: Record<string, any> }, callbacks: { onSuccess: () => void; onError: (e: unknown) => void }) => void;
    isPending: boolean;
  };
}

export const SurveyFormWrapper = ({ clientForm, submitOverride }: SurveyFormWrapperProps) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isV2 = (clientForm.formLayoutVersion ?? 1) >= 2;
  const classes = isV2 ? classesV2 : classesV1;

  const defaultSubmitMutation = useSubmitSurveyResponse({ opcode: 'SubmitSurvey001' });
  const submitSurveyMutation = submitOverride ?? defaultSubmitMutation;
  useRegisterCustomQuestionTypes({ species: clientForm.species, clientForm });

  // Apply basic Android input fixes
  useMobileInputFix();

  const survey = useMemo(() => {
    try {
      // Handle both string and object formats for surveyJson
      const parsedJson = typeof clientForm.surveyJson === 'string'
        ? JSON.parse(clientForm.surveyJson)
        : clientForm.surveyJson;

      // Create survey model from the surveyJson
      const surveyModel = new Model(parsedJson);

      // V1: apply the hardcoded theme; V2: apply the user's saved theme
      if (!isV2) {
        surveyModel.applyTheme(getThemeJson(clientForm.backgroundThemeId));
      } else if (clientForm.surveyThemeJson) {
        surveyModel.applyTheme(clientForm.surveyThemeJson);
      }

      // Configure survey settings
      surveyModel.showCompletedPage = false;
      surveyModel.showProgressBar = 'top';
      surveyModel.progressBarType = 'buttons';

      // Mobile-specific configurations for Android tablet support
      surveyModel.focusFirstQuestionAutomatic = false;
      surveyModel.checkErrorsMode = 'onValueChanged';

      // The answer ceilings the forms API enforces, checked on the field first
      applyResponseLimits(surveyModel);

      // Handle survey completion
      surveyModel.onComplete.add((sender) => {
        const surveyData = sender.data;

        // Submit the survey response
        submitSurveyMutation.mutate(
          {
            uuid: clientForm.uuid,
            responseJson: surveyData,
          },
          {
            onSuccess: () => {
              setIsCompleted(true);
              setError(null);
            },
            onError: (error) => {
              console.error('Error submitting survey:', error);
              setError('Failed to submit survey. Please try again.');
            },
          }
        );
      });

      return surveyModel;
    } catch (err) {
      console.error('Error creating survey:', err);
      setError('Failed to load survey. Please refresh the page and try again.');
      return null;
    }
  }, [clientForm.surveyJson, clientForm.uuid, clientForm.backgroundThemeId, clientForm.surveyThemeJson, isV2, submitSurveyMutation]);

  if (error) {
    return (
      <Stack className={classes.errorContainer}>
        <Alert color="red" title="Error">
          {error}
        </Alert>
        <Button onClick={() => window.location.reload()}>Refresh Page</Button>
      </Stack>
    );
  }

  if (isCompleted) {
    return (
      <Stack className={classes.completedContainer}>
        <Text className={classes.completedTitle}>Thank you for completing the form!</Text>
        <Text className={classes.completedMessage}>Your responses have been submitted successfully. You may now close this window.</Text>
      </Stack>
    );
  }

  if (submitSurveyMutation.isPending) {
    return (
      <Stack className={classes.submittingContainer}>
        <Text className={classes.submittingText}>Submitting your responses...</Text>
      </Stack>
    );
  }

  return <Stack className={classes.surveyContainer}>{survey ? <Survey model={survey} /> : null}</Stack>;
};
